// All Tenants-page API calls. Each function maps to one backend endpoint and
// goes through the shared apiService (never raw axios/fetch).

import { apiService } from "src/services";
import {
	IDecommissionRequest,
	IDraftedEmail,
	DeactivationScope,
	IHealthSettingsPayload,
	IHealthSweepSummary,
	ITenant,
	ITenantCreatePayload,
	ITenantHealth,
	ITenantReminders,
	ITenantUpdatePayload,
} from "../models/interfaces";

import { IEnvelope, unwrap, apiErrorMessage } from "src/utils/kcc";

// POST /tenants/ returns the created tenant plus the drafted provisioning email
// and (when RLS provisioning is configured) a one-time Postgres connection URI.
// provisioning.rls_role reports the role-provisioning outcome IN-BAND (the BE
// added it because a silent 200 hid every provisioning failure): disabled |
// created | skipped | failed — "failed" must be surfaced to the operator.
export type ITenantCreateResult = ITenant & {
	drafted_email?: IDraftedEmail;
	pg_uri?: string;
	provisioning?: { rls_role: "disabled" | "created" | "skipped" | "failed" };
};

// Re-exported so existing consumers (TenantDetail sub-tabs) keep importing it
// from here; the implementation lives in the shared KCC util.
export { apiErrorMessage };

// ── Provisioning / decommission emails (preview + send, 2026-09-09) ──────────
// CTPL sends these branded via SMTP like every other email (the Outlook mailto
// drafts are parked). dry_run returns the built email for the preview modal.

export interface ITenantEmailPreview {
	sent: false;
	dry_run: true;
	to: string[];
	cc: string[];
	subject: string;
	body_html: string;
}

export interface ITenantEmailSendPayload {
	to?: string[];
	cc?: string[];
	custom_subject?: string;
	custom_note?: string;
}

const tenantEmail = async (
	kind: "provisioning" | "decommission",
	tenantId: string,
	payload: ITenantEmailSendPayload & { dry_run?: boolean },
): Promise<ITenantEmailPreview | { sent: boolean; to: string[]; cc: string[] }> => {
	const res = await apiService.post<IEnvelope<ITenantEmailPreview>, typeof payload>(
		`/tenants/${tenantId}/${kind}/email`,
		payload,
		{ timeout: 60000 },
	);
	return unwrap(res);
};

export const previewProvisioningEmail = (tenantId: string) =>
	tenantEmail("provisioning", tenantId, { dry_run: true }) as Promise<ITenantEmailPreview>;
export const sendProvisioningEmail = (tenantId: string, payload: ITenantEmailSendPayload) =>
	tenantEmail("provisioning", tenantId, payload);
export const previewDecommissionEmail = (tenantId: string) =>
	tenantEmail("decommission", tenantId, { dry_run: true }) as Promise<ITenantEmailPreview>;
export const sendDecommissionEmail = (tenantId: string, payload: ITenantEmailSendPayload) =>
	tenantEmail("decommission", tenantId, payload);

// GET /tenants/ — list all tenants. The backend wraps results in ResponseModel
// ({ data, message }); the transformer unwraps it. Trailing slash matches the
// backend route exactly (avoids a 307 redirect under CORS).
export const getTenants = async (): Promise<unknown> => {
	return await apiService.get<unknown>("/tenants/");
};

// GET /tenants/health — health state for every tenant (FR-13).
export const getTenantsHealth = async (): Promise<ITenantHealth[]> => {
	const res = await apiService.get<IEnvelope<ITenantHealth[]>>("/tenants/health");
	return unwrap<ITenantHealth[]>(res) ?? [];
};

// PUT /tenants/{tenantId}/health — snooze / enable / probe-URL override (partial).
export const updateHealthSettings = async (
	tenantId: string,
	payload: IHealthSettingsPayload,
): Promise<ITenantHealth> => {
	const res = await apiService.put<IEnvelope<ITenantHealth>, IHealthSettingsPayload>(
		`/tenants/${tenantId}/health`,
		payload,
	);
	return unwrap<ITenantHealth>(res);
};

// POST /tenants/{tenantId}/health/check — probe ONE tenant now (Overview's
// Check now). Returns the run summary plus that tenant's refreshed health.
export const checkTenantHealth = async (
	tenantId: string,
): Promise<{ summary: IHealthSweepSummary; health: ITenantHealth | null }> => {
	const res = await apiService.post<
		IEnvelope<{ summary: IHealthSweepSummary; health: ITenantHealth | null }>,
		Record<string, never>
	>(`/tenants/${tenantId}/health/check`, {});
	return unwrap<{ summary: IHealthSweepSummary; health: ITenantHealth | null }>(res);
};

// POST /tenants/health/run — run the 5-minute sweep on demand.
export const runHealthSweep = async (): Promise<IHealthSweepSummary> => {
	const res = await apiService.post<IEnvelope<IHealthSweepSummary>, Record<string, never>>(
		"/tenants/health/run",
		{},
	);
	return unwrap<IHealthSweepSummary>(res);
};

// GET /tenants/validation-config — validation rules the FE mirrors client-side
// (allowed POC email domains). Single source of truth is the backend setting.
export interface IValidationConfig {
	allowed_poc_domains: string[];
}

export const getValidationConfig = async (): Promise<IValidationConfig> => {
	const res = await apiService.get<IEnvelope<IValidationConfig>>("/tenants/validation-config");
	return unwrap<IValidationConfig>(res);
};

// POST /tenants/ — onboard a new tenant; the backend drafts the provisioning
// email and returns it alongside the created tenant.
export const createTenant = async (payload: ITenantCreatePayload): Promise<ITenantCreateResult> => {
	const res = await apiService.post<IEnvelope<ITenantCreateResult>, ITenantCreatePayload>("/tenants/", payload);
	return unwrap<ITenantCreateResult>(res);
};

// PATCH /tenants/{tenantId} — edit CST POC / project code / expiry (Overview inline
// edit, FR-2/FR-8). Only the fields present are applied server-side.
export const updateTenant = async (tenantId: string, payload: ITenantUpdatePayload): Promise<ITenant> => {
	const res = await apiService.patch<IEnvelope<ITenant>, ITenantUpdatePayload>(`/tenants/${tenantId}`, payload);
	return unwrap<ITenant>(res);
};

// PUT /tenants/{tenantId}/reminders — replace the expiry/purge reminder thresholds
// (FR-6). KCC stores them; the Kural app/scheduler sends the notifications.
export const updateReminders = async (tenantId: string, reminders: ITenantReminders): Promise<ITenant> => {
	const res = await apiService.put<IEnvelope<ITenant>, ITenantReminders>(`/tenants/${tenantId}/reminders`, reminders);
	return unwrap<ITenant>(res);
};

// PUT /tenants/{tenantId}/ai-total-limit — set the tenant-wide AI spend cap (FR-11).
// Scope-based daily/weekly caps; null amounts clear the cap for that period.
export const updateAiTotalLimit = async (
	tenantId: string,
	payload: { scope: "tenant" | "profile"; daily_amount: number | null; weekly_amount: number | null; on_breach: "warn" | "restrict" },
): Promise<ITenant> => {
	const res = await apiService.put<IEnvelope<ITenant>, typeof payload>(`/tenants/${tenantId}/ai-total-limit`, payload);
	return unwrap<ITenant>(res);
};

// ── Lifecycle (FR-3 deactivate/reactivate, FR-5 decommission 4-eyes) ──────────

// POST /tenants/{tenantId}/deactivate — block access for the chosen audience
// (client | zs | both). Reversible; no purge clock.
export const deactivateTenant = async (
	tenantId: string,
	scope: DeactivationScope,
	reason?: string,
): Promise<ITenant> => {
	const res = await apiService.post<IEnvelope<ITenant>, { scope: DeactivationScope; reason?: string }>(
		`/tenants/${tenantId}/deactivate`,
		{ scope, reason },
	);
	return unwrap<ITenant>(res);
};

// POST /tenants/{tenantId}/reactivate — restore a deactivated tenant to active.
export const reactivateTenant = async (tenantId: string): Promise<ITenant> => {
	const res = await apiService.post<IEnvelope<ITenant>, Record<string, never>>(
		`/tenants/${tenantId}/reactivate`,
		{},
	);
	return unwrap<ITenant>(res);
};

// POST /tenants/{tenantId}/decommission — initiate a decommission. Records the
// request, drafts the cloud-team email, and marks the tenant decommissioned.
// The cloud team approves + executes the purge over email (no in-app approval).
export const requestDecommission = async (tenantId: string, reason?: string): Promise<IDecommissionRequest> => {
	const res = await apiService.post<IEnvelope<IDecommissionRequest>, { reason?: string }>(
		`/tenants/${tenantId}/decommission`,
		{ reason },
	);
	return unwrap<IDecommissionRequest>(res);
};

// DELETE /tenants/{tenantId} — permanently delete a tenant record and its child
// data (plans, cost, decommission requests) + branding logos. Irreversible.
export const deleteTenant = async (tenantId: string): Promise<void> => {
	await apiService.delete(`/tenants/${tenantId}`);
};

// GET /tenants/{tenantId}/decommission — most-recent request (tracking) for the
// detail header / Governance tab; null when the tenant was never decommissioned.
export const getDecommission = async (tenantId: string): Promise<IDecommissionRequest | null> => {
	const res = await apiService.get<IEnvelope<IDecommissionRequest | null>>(`/tenants/${tenantId}/decommission`);
	return unwrap<IDecommissionRequest | null>(res);
};
